// @flow
import React from 'react';
import { Link } from 'react-router-dom';
import { updateLocationSearch } from '../../../utils/url';
import styles from './BurgerMenuItem.module.css';
import type { MenuItem } from '../menuItems';

const MENU_ITEM_HEIGHT = 47;

type Props = {|
  +children: Array<MenuItem>,
  +name: string,
  +live: boolean,
  +selectedCategoryId: string | null,
  +toggleMenu: (void) => void,
  +expandedCategories: Set<string | null>,
  +primaryItem: boolean,
  +categoryId: string | null,
  +toggleCategoryExpansion: (string | null) => void,
  +path?: string,
  +isOpenByDefault?: boolean,
  +isNotCategory: boolean,
  +handleCategoryTracking: (string) => void,
|};

/*
 * recursively counts number of displayed children categories
 */
function countDisplayedChildrenCategories(
  expandedCategories,
  childrenCategories
) {
  if (childrenCategories.length === 0) return 0;

  let count = 0;

  childrenCategories.forEach((childCategory) => {
    // if current category is expanded, add its children to count,
    // and count those children's children that are displayed
    if (expandedCategories.has(childCategory.id)) {
      count += childCategory.children.length;
      count += countDisplayedChildrenCategories(
        expandedCategories,
        childCategory.children
      );
    }
  });

  return count;
}

function BurgerMenuItem(props: Props) {
  const {
    children,
    name,
    primaryItem,
    path,
    live,
    categoryId,
    selectedCategoryId,
    toggleMenu,
    expandedCategories,
    toggleCategoryExpansion,
    isNotCategory,
    handleCategoryTracking,
    isChild,
    isSecondChild,
  } = props;
  const isCategoryExpanded = expandedCategories.has(categoryId);
  const hasChildren = children.length > 0;
  // if current category is expanded, count number of categories that are currently displayed within it
  const displayedChildrenCategoryCount = isCategoryExpanded
    ? children.length +
      countDisplayedChildrenCategories(expandedCategories, children)
    : 0;
  const childrenContainerHeight =
    displayedChildrenCategoryCount * MENU_ITEM_HEIGHT;
  const toggleMenuItemButton = isCategoryExpanded ? (
    <i className="fal fa-minus" />
  ) : (
    <i className="fal fa-plus" />
  );

  function handleClickCategory() {
    toggleMenu();
    handleCategoryTracking(name);
  }

  return (
    <li
      className={`${styles.burgerMenuItem} ${
        primaryItem ? styles.primaryItem : ''
      }`}
    >
      <div
        className={`${styles.menuItemControls} ${
          isSecondChild ? styles.nested : ''
        }`}
      >
        {isNotCategory ? (
          <a className={styles.menuItemLink} href={path}>
            {name}{' '}
            {live && (
              <div className={styles.liveIconContainer}>
                <span className={styles.liveIcon}>live</span>
              </div>
            )}
          </a>
        ) : (
          <Link
            className={`${styles.menuItemLink} ${
              selectedCategoryId === (categoryId && categoryId.toString())
                ? styles.selectedCategory
                : ''
            }`}
            onClick={handleClickCategory}
            to={updateLocationSearch({
              category: categoryId,
              product_link: null,
            })}
          >
            {name}
          </Link>
        )}
        {hasChildren && (
          <button
            className={`${styles.toggleMenuItemButton} naked-button`}
            onClick={() => toggleCategoryExpansion(categoryId)}
            type="button"
          >
            {toggleMenuItemButton}
            <span className="sr-only">
              {isCategoryExpanded ? 'collapse' : 'expand'} menu item children
            </span>
          </button>
        )}
      </div>

      <ul
        className={styles.childrenContainer}
        style={{
          height: childrenContainerHeight,
        }}
      >
        {children.map((child) => (
          <BurgerMenuItem
            key={child.id}
            selectedCategoryId={selectedCategoryId}
            children={child.children}
            categoryId={child.id}
            name={child.name}
            isChild
            isSecondChild={isChild}
            path={child.path}
            live={false}
            toggleMenu={toggleMenu}
            expandedCategories={expandedCategories}
            primaryItem={false}
            toggleCategoryExpansion={toggleCategoryExpansion}
            isNotCategory={child.isNotCategory || false}
            handleCategoryTracking={handleCategoryTracking}
          />
        ))}
      </ul>
    </li>
  );
}

export default BurgerMenuItem;
